/* eslint-disable no-use-before-define */
import { fetchPage } from '@drapejs/core';
import { Invoker, defineReceiver } from '@drapejs/invoker';
import {
  configure,
  websites,
  search,
  sales,
  batch,
} from '@distancify/drapejs-litium';
import { isNode } from '@drapejs/runtime-context';
import {
  getCompareProductData,
  getCompareProductDataReceiver,
} from './commands/compare-products';
import * as user from './commands/user';
import * as didriksonsWebsites from './commands/websites';
import * as didriksonsSearch from './commands/search';
import * as localSales from './commands/sales';
import * as externalPayments from './commands/external-payments';
import * as category from './commands/category';
import * as productBlock from './commands/product-block';
import * as productsCarouselBlock from './commands/products-carousel-block-tab-products'
import * as media from './commands/media';
import * as easycom from './commands/easycom';
import * as voyado from './commands/voyado';
import * as ugcmedia from './commands/ugcmedia';
import * as recentlyVisitedProducts from './commands/recentlyVisitedProducts';
import * as relatedProducts from './commands/relatedProducts';

import { buildUrlPathWithQuery } from './utils';

export default function (invoker: Invoker) {
  invoker
    .configureCommand(fetchPage)
    .addReceiver(
      defineReceiver(fetchPage, async function (command: any, data: any) {
        const { path } = <any>command;
        command.url = buildUrlPathWithQuery(
          `${(<any>command).protocol}//${(<any>command).host}${(<any>command).path}`,
          (<any>command).query
        );

        if (path.startsWith('/klarna-checkout/external-payment') || path.startsWith('/distancify/api/paypal')) {
          this.break();
          data = {
            fields: null,
            http_location: '',
            http_status: 200,
            robots: 'noindex, nofollow',
            systemId: null,
            title: 'External payment',
            type: 'ExternalPayment',
          };
          await this.cache.setItem(command.cacheKey, data);

          if(!isNode()){
            await sales.scheduleRefreshCartReceiver.call(this, command, data);
          }

          return data;
        }

        return data;
      })
    )
    .addReceiver(
      defineReceiver(fetchPage, async function (command: any, data) {
        if(!isNode()){
          await sales.scheduleRefreshCartReceiver.call(this, command, data);
          await user.schedule.call(this, command, data);
        }
        return data;
      })
    )
    .addReceiver(websites.fetchPageReceiver)
    .addReceiver(category.schedule)
    .addReceiver(
      defineReceiver(fetchPage, async function (command: any, data) {
        if(!isNode()){
          await recentlyVisitedProducts.handleRecentlyVisitedProducts.call(this, command, data);
        }
        return data;
      })
    )    
    .addReceiver(productBlock.schedule)
    .addReceiver(productsCarouselBlock.schedule)
    .addReceiver(relatedProducts.schedule)
    .addReceiver(batch.executeBathQueryReceiver)
    .addReceiver(
      defineReceiver(fetchPage, async function (command: any, data) {
        if(!isNode()){
          await user.process.call(this, command, data);
        }
        return data;
      })
    )    
    .addReceiver(category.process);

  invoker
    .configureCommand(localSales.commands.getKlarnaOrder)
    .addReceiver(localSales.receivers.getKlarnaOrder);

  invoker
    .configureCommand(localSales.commands.applyVoucherCode)
    .addReceiver(localSales.receivers.applyVoucherCode);

  invoker
    .configureCommand(localSales.commands.clearCart)
    .addReceiver(localSales.receivers.clearCart);

  invoker
    .configureCommand(localSales.commands.setAddressFromKlarna)
    .addReceiver(localSales.receivers.setAddressFromKlarna);

  invoker
    .configureCommand(localSales.commands.setKlarnaCustomerType)
    .addReceiver(localSales.receivers.setKlarnaCustomerType);

  invoker
    .configureCommand(localSales.commands.refreshCart)
    .addReceiver(localSales.receivers.refreshCart);

  invoker
    .configureCommand(localSales.commands.subscribeToVoyadoMarketingPromotions)
    .addReceiver(localSales.receivers.subscribeToVoyadoMarketingPromotions);    

  invoker
    .configureCommand(localSales.commands.setNewsletterSubscriptionStatus)
    .addReceiver(localSales.receivers.setNewsletterSubscriptionStatus);    

  invoker
    .configureCommand(externalPayments.commands.executeLitiumCallback)
    .addReceiver(externalPayments.receivers.executeLitiumCallback);

  invoker
    .configureCommand(externalPayments.commands.getReceipt)
    .addReceiver(externalPayments.receivers.getReceipt);

  invoker
    .configureCommand(batch.scheduleQuery)
    .addReceiver(batch.scheduleQueryReceiver);

  invoker
    .configureCommand(batch.executeBatchQuery)
    .addReceiver(batch.executeBathQueryReceiver);

  invoker
    .configureCommand(sales.scheduleRefreshCart)
    .addReceiver(sales.scheduleRefreshCartReceiver);

  invoker
    .configureCommand(sales.refreshCart)
    .addReceiver(sales.refreshCartReceiver);

  invoker
    .configureCommand(sales.addToCart)
    .addReceiver(sales.addToCartReceiver);

  invoker
    .configureCommand(sales.setRowQuantity)
    .addReceiver(sales.setRowQuantityReceiver);

  invoker
    .configureCommand(sales.subscribeToNewsletter)
    .addReceiver(sales.subscribeToNewsletterReceiver);

  invoker
    .configureCommand(sales.getOrder)
    .addReceiver(sales.getOrderReceiver);

  invoker
    .configureCommand(sales.resetCartToAvailableStock)
    .addReceiver(sales.resetCartToAvailableStockReceiver);

  invoker
    .configureCommand(search.commands.fetchCategoryProducts)
    .addReceiver(search.receivers.fetchCategoryProducts);

  invoker
    .configureCommand(search.commands.fetchMoreCategoryProducts)
    .addReceiver(search.receivers.fetchMoreCategoryProducts);

  invoker
    .configureCommand(didriksonsSearch.commands.performQuickSearch)
    .addReceiver(didriksonsSearch.receivers.performQuickSearch);

  invoker
    .configureCommand(getCompareProductData)
    .addReceiver(getCompareProductDataReceiver);

  invoker
    .configureCommand(user.commands.setCountry)
    .addReceiver(user.receivers.setCountry);

  invoker.configureCommand(media.commands.refreshMedia)
    .addReceiver(media.receivers.refreshMedia);

  invoker.configureCommand(media.commands.login)
    .addReceiver(media.receivers.login);

  invoker.configureCommand(media.commands.logout)
    .addReceiver(media.receivers.logout);

  invoker.configureCommand(easycom.commands.getReturnToken)
    .addReceiver(easycom.receivers.getReturnToken);

  invoker.configureCommand(voyado.commands.getContactId)
    .addReceiver(voyado.receivers.getContactId);

    
  invoker.configureCommand(voyado.commands.setCartRef)
  .addReceiver(voyado.receivers.setCartRef);
    
  invoker.configureCommand(ugcmedia.commands.ugcMediaSearch)
    .addReceiver(ugcmedia.receivers.ugcMediaSearch);

  invoker
    .configureCommand(didriksonsWebsites.commands.countrySwitcher)
    .addReceiver(didriksonsWebsites.receivers.countrySwitcher);

  configure({
    fetchPage: `
      title
      description
    `,
    cart: getCartFields(),
    cartRow: getCartRowFields(),
    receipt: '',
  });
}

function getCartFields() {
  return `
    customerAddress {
      email
      firstName
      lastName
      address1
      address2
      city
      postalCode
      phone
      state
      country
    }
    countryId
    ecommerceFeaturesEnabled
    isSplitCountryDelivery
    isSubscribedToNewsletter
    voyadoCartRef
    deliveries {
      id
      selectedDeliveryOption
      selectedDeliveryAgent
      selectedDeliveryOptionPrice
      selectedDeliveryOptionOriginalPrice
      inventoryId
      inventoryCountry
      inventoryZip
      total
      weight
      volume
    }
    newsletterConsent {
      agreedToConsent
      previouslyAgreedToConsent
      subscribeToNewsletterText
      freeShippingText
      pageLinkText
      pageLinkUrl
    }
  `;
}

function getCartRowFields() {
  return `
    size
    color
    unitListPrice
    baseProductId
    modelNumber
    unitCampaignPrice {
      price
      name
    }
    shippingInterval {
      inventoryId
      minimumDeliveryTimeInDays
      maximumDeliveryTimeInDays
    }    
  `;
}
